import React, { useMemo, useState } from "react";
import MenuSlider from "./MenuSlider";

import "./menu.scss";
import bg from "./d.jpg";
import { usePizzas } from "../hooks/usePizzas";
import { pizza } from "../Data/pizza"; // Fallback data
import { AiOutlineWhatsApp } from "react-icons/ai";
import { FiShoppingCart, FiX } from "react-icons/fi";

const Menu = () => {
  const { data: pizzas, isLoading: loading, error } = usePizzas();
  const [products, setProducts] = useState([]);
  // Simple cart: { [id]: { product, qty } }
  const [cart, setCart] = useState({});
  const [showCart, setShowCart] = useState(false);

  // Actualizar productos cuando las pizzas se cargan
  React.useEffect(() => {
    if (!loading) {
      if (pizzas && pizzas.length > 0) {
        console.log('✅ Mostrando productos desde Supabase:', pizzas.length);
        setProducts(pizzas);
      } else {
        console.log('⚠️ Usando datos locales de fallback');
        setProducts(pizza);
      }
    }
  }, [pizzas, loading, error]);

  const menu = [
    {
      id: 1,
      name: "Pizza",
    },
  ];

  const menuClickHandler = (data) => {
    if (data.name === "Pizza") {
      setProducts((pizzas && pizzas.length > 0) ? pizzas : pizza);
    }
  };

  const increment = (product) => {
    setCart((prev) => {
      const prevQty = prev[product.id]?.qty || 0;
      return {
        ...prev,
        [product.id]: { product, qty: prevQty + 1 },
      };
    });
  };

  const decrement = (product) => {
    setCart((prev) => {
      const current = prev[product.id]?.qty || 0;
      if (!current) return prev;
      const next = { ...prev };
      const newQty = current - 1;
      if (newQty <= 0) {
        delete next[product.id];
      } else {
        next[product.id] = { product, qty: newQty };
      }
      return next;
    });
  };

  const getQty = (id) => cart[id]?.qty || 0;

  const cartItems = useMemo(() => Object.values(cart), [cart]);
  const itemCount = useMemo(
    () => cartItems.reduce((sum, it) => sum + it.qty, 0),
    [cartItems]
  );
  const total = useMemo(
    () => cartItems.reduce((sum, it) => sum + (it.product.price_month || it.product.price) * it.qty, 0),
    [cartItems]
  );

  const formatCRC = (n) =>
    new Intl.NumberFormat("es-CR", {
      style: "currency",
      currency: "CRC",
      maximumFractionDigits: 0,
    }).format(n);

  const buildWhatsAppUrl = () => {
    if (!cartItems.length) return "#";
    const lines = cartItems
      .map(
        (it) => `• ${it.product.name_product || it.product.description || it.product.name} x${it.qty} - ${formatCRC(
          (it.product.price_month || it.product.price) * it.qty
        )}`
      )
      .join("\n");
    const message = `Hola, quiero hacer el siguiente pedido:\n${lines}\nTotal: ${formatCRC(
      total
    )}`;
    const encoded = encodeURIComponent(message);
    // Usar el mismo número de WhatsApp que el botón "Order Now"
    const phone = "50664521140";
    return `https://wa.me/${phone}?text=${encoded}`;
  };

  return (
    <section
      id="menu"
      className="foodMenu bgImg bgImgFixed"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <div className="container">
        <div className="row">
          <div className="foodMenuContainer">
            {/* Slider de menú español/inglés */}
            <MenuSlider />
            {/* Menú dinámico existente */}
            <h1 className="foodMenuContainerHeader">
              What will you <span className="color-grey">Eat</span> today?
            </h1>
            <div className="foodMenuContainerList">
              {menu.map((m) => (
                <div
                  className="foodMenuContainerItem"
                  key={m.id}
                  onClick={() => menuClickHandler(m)}
                >
                  {m.name}
                </div>
              ))}
            </div>
            {loading && (
              <div className="loadingMessage">
                <p>Cargando pizzas...</p>
              </div>
            )}
            
            {error && (
              <div className="errorMessage">
                <p>Error al cargar las pizzas: {error}</p>
              </div>
            )}
            
            <div className="foodMenu">
              {products.map((p) => (
                <div className="foodMenuDetails" key={p.id}>
                  <div className="foodMenuImg">
                    <img 
                      src={
                        p.product_images?.[0]?.image_url || 
                        p.image_url || 
                        p.imageUrl || 
                        // Usar imágenes locales basadas en el ID del producto
                        (p.id === 4 ? "/images/pizzaspromo_1.jpeg" :
                         p.id === 5 ? "/images/pizzaspromo_2.jpeg" :
                         p.id === 6 ? "/images/pizzaspromo_3.jpeg" :
                         p.id === 7 ? "/images/pizzaspromo_4.jpeg" :
                         p.id === 8 ? "/images/pizzatypes/pesto.jpeg" :
                         p.id === 9 ? "/images/pizzatypes/chef.jpeg" :
                         p.id === 10 ? "/images/pizzatypes/margarita.jpeg" :
                         p.id === 11 ? "/images/pizzhorno.jpg" :
                         p.id === 13 ? "/images/pizzasvarias.jpg" :
                         "/images/pizzatypes/suprema.jpeg")
                      } 
                      alt={p.description || p.name_product || p.name} 
                      className="bgImg"
                      onError={(e) => {
                        console.error('❌ Error cargando imagen:', e.target.src);
                        console.log('🔄 Usando imagen fallback suprema');
                        // Imagen por defecto si no se encuentra
                        e.target.src = '/images/pizzatypes/suprema.jpeg';
                      }}
                    />
                  </div>
                  <div className="productInfo">
                    <h1 className="productName">
                      {p.name_product || `Pizza Especial ${p.id}`}
                    </h1>
                    <p className="productDescription">{p.description || 'Deliciosa pizza artesanal'}</p>
                    <div className="productPrice">{`₡ ${(p.price_month || p.price)?.toLocaleString('es-CR')}`}</div>
                  </div>
                  <div className="addToCart">
                    <button
                      className="qtyBtn"
                      onClick={() => decrement(p)}
                      disabled={getQty(p.id) === 0}
                      aria-label={`Disminuir ${p.name}`}
                    >
                      -
                    </button>
                    <span className="qtyCount">{getQty(p.id)}</span>
                    <button
                      className="qtyBtn"
                      onClick={() => increment(p)}
                      aria-label={`Aumentar ${p.name}`}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {itemCount > 0 && (
              <button
                className="cartFloatingBtn"
                onClick={() => setShowCart(true)}
                aria-label="Abrir carrito"
              >
                <FiShoppingCart size={18} />
                <span>Tu pedido ({itemCount})</span>
              </button>
            )}

            {showCart && (
              <div
                className="cartDrawerOverlay"
                onClick={() => setShowCart(false)}
                role="presentation"
              >
                <div
                  className="cartDrawer"
                  onClick={(e) => e.stopPropagation()}
                  role="dialog"
                  aria-label="Carrito de compras"
                >
                  <div className="cartHeader">
                    <h2>Tu pedido</h2>
                    <button
                      className="iconBtn"
                      onClick={() => setShowCart(false)}
                      aria-label="Cerrar"
                    >
                      <FiX size={18} />
                    </button>
                  </div>
                  <div className="cartBody">
                    {cartItems.length === 0 ? (
                      <p className="empty">Tu carrito está vacío.</p>
                    ) : (
                      <ul>
                        {cartItems.map((it) => (
                          <li key={it.product.id} className="cartItem">
                            <div className="itemMain">
                              <span className="name">{it.product.name_product || it.product.description || it.product.name}</span>
                              <span className="price">
                                {formatCRC((it.product.price_month || it.product.price) * it.qty)}
                              </span>
                            </div>
                            <div className="itemActions">
                              <button
                                className="qtyBtn"
                                onClick={() => decrement(it.product)}
                                aria-label={`Disminuir ${it.product.name_product || it.product.description || it.product.name}`}
                              >
                                -
                              </button>
                              <span className="qtyCount">{it.qty}</span>
                              <button
                                className="qtyBtn"
                                onClick={() => increment(it.product)}
                                aria-label={`Aumentar ${it.product.name_product || it.product.description || it.product.name}`}
                              >
                                +
                              </button>
                              <button
                                className="removeBtn"
                                onClick={() =>
                                  setCart((prev) => {
                                    const next = { ...prev };
                                    delete next[it.product.id];
                                    return next;
                                  })
                                }
                                aria-label={`Quitar ${it.product.name_product || it.product.description || it.product.name}`}
                              >
                                <FiX />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="cartFooter">
                    <div className="total">
                      <strong>Total:</strong> {formatCRC(total)}
                    </div>
                    <a
                      className={`waBtn ${cartItems.length === 0 ? "disabled" : ""}`}
                      href={buildWhatsAppUrl()}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => {
                        if (!cartItems.length) e.preventDefault();
                      }}
                    >
                      <AiOutlineWhatsApp size={20} />
                      <span>Enviar por WhatsApp</span>
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Menu;
